import React, { useEffect } from "react";
import type { ScheduleSlotMenuItem } from "../ScheduleSlotActionMenu";

type Props = {
  open: boolean;
  title: string;
  items: ScheduleSlotMenuItem[];
  onClose: () => void;
};

const toneClasses: Record<NonNullable<ScheduleSlotMenuItem["tone"]>, string> = {
  default: "text-slate-700 active:bg-slate-50",
  primary: "text-[#3182CE] active:bg-sky-50",
  danger: "text-red-700 active:bg-red-50",
};

/**
 * Mobile version of ScheduleSlotActionMenu: same title and options, anchored to the
 * bottom of the screen because the desktop menu is positioned by the mouse.
 */
const MobileSlotActionSheet: React.FC<Props> = ({ open, title, items, onClose }) => {
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open || items.length === 0) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center">
      <button
        type="button"
        aria-label="Fechar"
        className="absolute inset-0 bg-slate-900/40"
        onClick={onClose}
      />
      <div
        role="menu"
        aria-label={title}
        className="relative w-full max-w-lg overflow-hidden rounded-t-2xl border border-slate-200 bg-white pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-2xl"
      >
        <div className="border-b border-slate-100 px-5 py-4 text-sm font-semibold text-slate-600">
          {title}
        </div>
        <div className="flex flex-col p-2">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                onClose();
                item.onClick();
              }}
              className={`w-full rounded-lg px-4 py-3.5 text-left text-[15px] font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                toneClasses[item.tone ?? "default"]
              }`}
            >
              {item.label}
            </button>
          ))}
          <button
            type="button"
            onClick={onClose}
            className="mt-1 w-full rounded-lg px-4 py-3.5 text-left text-[15px] font-medium text-slate-500 active:bg-slate-50"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileSlotActionSheet;
